import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";
import { CONTACT, waLink } from "../lib/constants";
import { sendContact } from "../lib/api";
import GoldDivider from "../components/GoldDivider";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs requis");
      return;
    }
    setSending(true);
    try {
      await sendContact(form);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="hero-noir py-20 px-4 md:px-8 text-center">
        <span className="text-xs uppercase tracking-[0.3em] text-[#B8860B]">Contact</span>
        <h1 className="font-display text-4xl md:text-6xl text-white mt-3 mb-4">Parlons-en</h1>
        <GoldDivider />
      </section>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl text-[#0A0A0A] mb-4">Nous contacter</h2>
          <p className="text-neutral-600 mb-8 leading-relaxed">
            Une question, un conseil, un suivi de commande ? Notre équipe vous répond avec plaisir.
            Le plus rapide reste WhatsApp !
          </p>

          <div className="space-y-4 mb-8">
            <a href={waLink("Bonjour MaxiChoix !")} target="_blank" rel="noreferrer"
               className="flex items-center gap-4 p-4 border border-neutral-100 rounded-sm hover:border-[#B8860B] transition" data-testid="contact-whatsapp">
              <div className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center"><MessageCircle size={22} /></div>
              <div><p className="font-medium text-[#0A0A0A]">WhatsApp</p><p className="text-sm text-neutral-500">Réponse rapide</p></div>
            </a>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 p-4 border border-neutral-100 rounded-sm hover:border-[#B8860B] transition">
              <div className="w-12 h-12 rounded-full border border-[#B8860B]/30 text-[#B8860B] flex items-center justify-center"><Phone size={20} /></div>
              <div><p className="font-medium text-[#0A0A0A]">Téléphone</p><p className="text-sm text-neutral-500">{CONTACT.phoneIntl}</p></div>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 p-4 border border-neutral-100 rounded-sm hover:border-[#B8860B] transition">
              <div className="w-12 h-12 rounded-full border border-[#B8860B]/30 text-[#B8860B] flex items-center justify-center"><Mail size={20} /></div>
              <div><p className="font-medium text-[#0A0A0A]">Email</p><p className="text-sm text-neutral-500 break-all">{CONTACT.email}</p></div>
            </a>
            <div className="flex items-center gap-4 p-4 border border-neutral-100 rounded-sm">
              <div className="w-12 h-12 rounded-full border border-[#B8860B]/30 text-[#B8860B] flex items-center justify-center"><MapPin size={20} /></div>
              <div><p className="font-medium text-[#0A0A0A]">Localisation</p><p className="text-sm text-neutral-500">{CONTACT.city}</p></div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-[#FBF9F4] p-8 rounded-lg" data-testid="contact-form">
          <h3 className="font-display text-2xl text-[#0A0A0A] mb-6">Envoyez-nous un message</h3>
          <div className="space-y-4">
            <input value={form.name} onChange={set("name")} placeholder="Nom complet *"
              className="w-full border border-neutral-200 rounded-sm py-3 px-4 text-sm focus:outline-none focus:border-[#B8860B] bg-white" data-testid="contact-name" />
            <input value={form.email} onChange={set("email")} type="email" placeholder="Email *"
              className="w-full border border-neutral-200 rounded-sm py-3 px-4 text-sm focus:outline-none focus:border-[#B8860B] bg-white" data-testid="contact-email" />
            <input value={form.phone} onChange={set("phone")} placeholder="Téléphone"
              className="w-full border border-neutral-200 rounded-sm py-3 px-4 text-sm focus:outline-none focus:border-[#B8860B] bg-white" data-testid="contact-phone" />
            <textarea value={form.message} onChange={set("message")} rows={5} placeholder="Votre message *"
              className="w-full border border-neutral-200 rounded-sm py-3 px-4 text-sm focus:outline-none focus:border-[#B8860B] bg-white" data-testid="contact-message" />
            <button type="submit" disabled={sending} className="btn-gold w-full py-4 rounded-sm disabled:opacity-60" data-testid="contact-submit">
              {sending ? "Envoi…" : <>Envoyer le message <Send size={16} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
